// The carnet de chant groups songs into thematic sections (the "table des fiches
// par cote", carnet pages 909-917). The leading letter of the SECLI cote heads a
// section, so it doubles as the song's theme — and the early letters even map to
// mass moments (A=entrée, B=offertoire, D=communion) while E-J map to seasons.
// All section headers A-W are confirmed from the songbook itself.
export const CARNET_CATEGORIES: Record<string, string> = {
  A: "Ouverture, louange",
  B: "Procession des offrandes",
  D: "Communion, action de grâce",
  E: "Avent",
  F: "Noël, Épiphanie",
  G: "Carême, conversion, réconciliation",
  H: "Passion, Croix, Rédemption",
  I: "Pâques, baptême, dimanche",
  J: "Ascension, Ciel, promesse",
  K: "Pentecôte, Saint-Esprit, Église",
  M: "Trinité, Fête-Dieu, Sacré-Cœur",
  O: "Mariage",
  P: "Prière, épreuve, travail",
  S: "Défunts, sépultures",
  T: "Église, mission, évangélisation",
  U: "Pèlerinages, acclamations",
  V: "Vierge Marie",
  W: "Saints et saintes, Toussaint",
};

// Cotes whose letter is NOT its own section (C, L, R, X… "float"): the songbook
// files them under another theme. Pinned here from where they actually appear.
const CARNET_OVERRIDES: Record<string, string> = {
  "C 556": "Ouverture, louange",
  "C 114": "Pèlerinages, acclamations",
  "C 146": "Église, mission, évangélisation",
  "C 121": "Procession des offrandes",
  "L 521": "Carême, conversion, réconciliation",
  "L 124": "Prière, épreuve, travail",
  "L 82": "Pèlerinages, acclamations",
  "C 153": "Ouverture, louange",
  "R 50": "Pentecôte, Saint-Esprit, Église",
  "X 57": "Pèlerinages, acclamations",
  "SM 177": "Église, mission, évangélisation",
  "SM 175": "Prière, épreuve, travail",
};

// "SM" is a label prefix (Studio SM), not the S=Défunts section — never map it by letter.
const EXCLUDE = new Set(["SM"]);

// Songs with no cote (the local Vierge & saints supplement, pages 1033-1047),
// categorised from their thematic section / title where it's unambiguous.
const CARNET_TITLE_CATEGORIES: Record<string, string> = {
  "Cantique à Saint-Laurent": "Saints et saintes, Toussaint",
  "Cantique à Saint-Pierre": "Saints et saintes, Toussaint",
  "Cantique à Saint-Léry": "Saints et saintes, Toussaint",
  "Ô Sainte Anne, ô Marie": "Vierge Marie",
  "Sainte Vierge, ma Mère": "Vierge Marie",
  "Réjouis-toi, Marie, tu es la joie de ton Seigneur": "Vierge Marie",
  "Viens, esprit de sainteté": "Pentecôte, Saint-Esprit, Église",
  "Viens, esprit de lumière": "Pentecôte, Saint-Esprit, Église",
  "Christ est ressuscité !": "Pâques, baptême, dimanche",
  "A toi, la gloire": "Pâques, baptême, dimanche",
  "Baptisés en Jésus": "Pâques, baptême, dimanche",
  "Alléluia, Proclamez que le Seigneur est bon (Ps 118)": "Ouverture, louange",
  "Béni soit le Nom du Seigneur": "Ouverture, louange",
  "Sa Parole est lumière": "Ouverture, louange",
};

export function categoryForTitle(title?: string): string | undefined {
  return title ? CARNET_TITLE_CATEGORIES[title] : undefined;
}

// Theme for a cote like "V 313", "BL 76", "C 556" — by an explicit pin, else the
// section its (base) letter heads. Variant prefixes (BL, SL, ELH…) fall back to
// their first letter; floating letters (C, L…) with no pin stay uncategorised.
export function categoryForRef(ref?: string): string | undefined {
  if (!ref) return undefined;
  if (CARNET_OVERRIDES[ref]) return CARNET_OVERRIDES[ref];
  const m = ref.match(/^([A-Za-z]+)/);
  if (!m) return undefined;
  const code = m[1].toUpperCase();
  if (EXCLUDE.has(code)) return undefined;
  return CARNET_CATEGORIES[code] || CARNET_CATEGORIES[code[0]] || undefined;
}
